import { create } from 'zustand'
import { zeroize } from '@/services/CryptoService'

type NoteKeyState = {
    /** In-memory map of note keys, keyed by note UUID. */
    keys: Map<string, Uint8Array>
}

type NoteKeyActions = {
    /**
     * Store a live note key for a given note.
     * The store takes ownership — caller must NOT zeroize the buffer after
     * passing it here.
     */
    setNoteKey: (noteId: string, key: Uint8Array) => void

    /** Retrieve the in-memory note key, or null if not present. */
    getNoteKey: (noteId: string) => Uint8Array | null

    /**
     * Zeroize and remove the note key for a given note.
     * Safe to call when the key is not present.
     */
    clearNoteKey: (noteId: string) => void

    /**
     * Zeroize and remove all in-memory note keys.
     * Call this on logout / session end.
     */
    clearAll: () => void
}

export const useNoteKeyStore = create<NoteKeyState & NoteKeyActions>()(
    (set, get) => ({
        keys: new Map(),

        setNoteKey: (noteId, key) => {
            set((state) => {
                const next = new Map(state.keys)
                // Zeroize any existing key for this note before replacing.
                const existing = next.get(noteId)
                if (existing) {
                    try { zeroize(existing) } catch { /* sodium may not be ready */ }
                }
                next.set(noteId, key)
                return { keys: next }
            })
        },

        getNoteKey: (noteId) => {
            return get().keys.get(noteId) ?? null
        },

        clearNoteKey: (noteId) => {
            set((state) => {
                const existing = state.keys.get(noteId)
                if (!existing) return state
                try { zeroize(existing) } catch { /* sodium may not be ready */ }
                const next = new Map(state.keys)
                next.delete(noteId)
                return { keys: next }
            })
        },

        clearAll: () => {
            get().keys.forEach((key) => {
                try { zeroize(key) } catch { /* sodium may not be ready */ }
            })
            set({ keys: new Map() })
        },
    }),
)

